import { fromPostgrest, guard, raise, type Result } from '@/lib/result';
import { supabase } from '@/lib/supabase';

import { mapMasjid, toMasjidRow, type Masjid, type MasjidCreateInput } from '../model/masjid';
import { mapMasjidMember, type MasjidMember } from '../model/masjid-member';

export const masjidRepository = {
  /** Masjids the caller owns OR co-manages (Phase 4 switcher set). */
  async listManaged(): Promise<Result<readonly Masjid[]>> {
    return guard(async () => {
      const { data, error } = await supabase.rpc('my_managed_masjids');
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapMasjid);
    });
  },

  async listMembers(masjidId: string): Promise<Result<readonly MasjidMember[]>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('masjid_members')
        .select('*, member:profiles!user_id(full_name)')
        .eq('masjid_id', masjidId)
        .order('created_at', { ascending: true });
      if (error) throw fromPostgrest(error);
      return ((data ?? []) as unknown[]).map(mapMasjidMember);
    });
  },

  async addMemberByPhone(masjidId: string, phone: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.rpc('add_masjid_member', {
        p_masjid_id: masjidId,
        p_phone: phone,
      });
      if (error) raise('server', error.message, error);
    });
  },

  async removeMember(memberId: string): Promise<Result<void>> {
    return guard(async () => {
      const { error } = await supabase.from('masjid_members').delete().eq('id', memberId);
      if (error) throw fromPostgrest(error);
    });
  },

  async create(input: MasjidCreateInput): Promise<Result<Masjid>> {
    return guard(async () => {
      const { data, error } = await supabase
        .from('masjids')
        .insert(toMasjidRow(input))
        .select('*')
        .single();
      if (error) throw fromPostgrest(error);
      return mapMasjid(data as unknown);
    });
  },
};
